import React, { FC, MouseEvent, memo, useCallback } from 'react';

import { noop } from 'lodash';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { FavoriteLoading } from 'components/lottie-animations/favorite-loading/favorite-loading';
import { Picture } from 'components/picture';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { parseExternalIncentiveData } from 'pages/loyalty/loyalty-incentives-components/external-incentive-card/parse-external-incentive-data';
import { ParsedRewardData } from 'pages/loyalty/loyalty-incentives-components/incentive-details/types';
import { OfferStatus } from 'pages/loyalty/loyalty-offers/types';
import { useAuthContext } from 'state/auth';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { IncentiveSource } from 'state/loyalty/types';
import { routes } from 'utils/routing';

import { ILoyaltyRewardsViewProps } from '../../loyalty-rewards/types';

import { ComingSoonIcon } from './ComingSoonIcon';
import { FavoriteIcon, FavouriteIconWrapper, FavouriteLoadingWrapper } from './FavouriteIcon';
import { LockIcon, LockIconWrapper } from './LockIcon';
import { PromoCodeIcon } from './PromoCodeIcon';
import {
  BackgroundImage,
  ClickContainer,
  DefaultBackground,
  DiscountedPriceText,
  FavButtonWrapper,
  LabelAndFavContainer,
  LabelBadge,
  LabelText,
  LabelWrapper,
  OfferCardContainer,
  OfferImageWrapper,
  PriceTextContainer,
  RewardText,
  SlashedPriceText,
  StyledRewardLock,
} from './loyalty-offers-card.styles';

export interface IOfferCardV2 {
  incentiveData: ParsedRewardData;
  handleClick: ILoyaltyRewardsViewProps['handleClick'];
  children?: React.ReactNode;
  isLocked?: boolean;
  isFavorited?: boolean;
  isFavoriteEnabled?: boolean;
  isFavoriteLoading?: boolean;
  handleToggleFavorite?: (sanityId: string, name: string) => void;
  isQuestChallengeIcentive?: boolean;
  isUpcomingOffer?: boolean;
}

export const OfferCardV2: FC<IOfferCardV2> = memo(
  ({
    incentiveData,
    handleClick,
    isLocked = false,
    isFavorited = false,
    isFavoriteEnabled = false,
    isFavoriteLoading = false,
    handleToggleFavorite = noop,
    isQuestChallengeIcentive = false,
    isUpcomingOffer = false,
  }) => {
    const { formatMessage } = useIntl();
    const { image, imageDescription, name, id, loyaltyEngineId, backgroundImage, metadata } =
      incentiveData;

    const { featureInvitationCode } = useFeatureInvitationCode();
    const questBenefit =
      featureInvitationCode?.invitationCodeBenefit &&
      featureInvitationCode?.invitationCodeBenefit[0];
    const { isAuthenticated } = useAuthContext();
    const navigate = useNavigate();

    const incentiveDetails = {
      name: isQuestChallengeIcentive ? questBenefit?.name?.locale : name,
      image: isQuestChallengeIcentive ? questBenefit?.image?.locale : image,
      backgroundImage: <DefaultBackground />,
    };

    let offerName = incentiveDetails.name;
    if (incentiveDetails.name && incentiveDetails.name.length > 70) {
      offerName = `${incentiveDetails.name.slice(0, 70)}...`;
    }

    const incentiveSanityId = id;
    const incentiveName = name;

    const showPromoCodeLabel = metadata?.some(
      data => data?.value === IncentiveSource.PROMOTION_CODE
    );
    const promoCodeLabel = showPromoCodeLabel
      ? formatMessage({ id: 'promoCodeBadgeLabel', defaultMessage: 'Promo Code' })
      : undefined;

    const handleFavoriteClick = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isAuthenticated()) {
          return navigate(routes.signUp);
        }
        handleToggleFavorite(id, name);
      },
      [handleToggleFavorite, id, name, isAuthenticated, navigate]
    );

    const renderOfferLabel = () => {
      if (promoCodeLabel) {
        return (
          <LabelBadge>
            <PromoCodeIcon width="13.33px" height="13.33px" />
            <LabelText>{promoCodeLabel}</LabelText>
          </LabelBadge>
        );
      }

      if (isUpcomingOffer) {
        return (
          <LabelBadge>
            <ComingSoonIcon width="13.33px" height="13.33px" />
            <LabelText>
              {formatMessage({ id: 'upcomingOfferLabel', defaultMessage: 'Coming Soon' })}
            </LabelText>
          </LabelBadge>
        );
      }

      return null;
    };

    let status: OfferStatus = OfferStatus.Available;
    if (isLocked) {
      status = OfferStatus.Locked;
    }
    if (isUpcomingOffer) {
      status = OfferStatus.ComingSoon;
    }

    return (
      <OfferCardContainer
        data-testid={`offers-list-${isLocked ? 'locked-offer-item' : 'offer-item'}`}
      >
        <ClickContainer
          onClick={handleClick({
            incentiveName,
            loyaltyEngineId,
            incentiveSanityId,
            isQuestChallengeIcentive,
            isUpcomingOffer,
            status,
          })}
          isQuestChallengeIcentive={isQuestChallengeIcentive}
        >
          <LabelAndFavContainer>
            <LabelWrapper>{renderOfferLabel()}</LabelWrapper>
            {isFavoriteEnabled && (
              <FavButtonWrapper
                data-testid={isFavorited ? 'fav-icon-selected' : 'fav-icon'}
                onClick={handleFavoriteClick}
                aria-label={formatMessage({
                  id: isFavorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
                })}
                isTransparent={true}
              >
                {isFavoriteLoading ? (
                  <FavouriteLoadingWrapper>
                    <FavoriteLoading height="2rem" width="2rem" />
                  </FavouriteLoadingWrapper>
                ) : (
                  <FavouriteIconWrapper>
                    <FavoriteIcon isFavorite={isFavorited} />
                  </FavouriteIconWrapper>
                )}
              </FavButtonWrapper>
            )}
          </LabelAndFavContainer>

          {incentiveDetails.image && (
            <OfferImageWrapper>
              {(isLocked || isQuestChallengeIcentive) && <StyledRewardLock />}

              {isQuestChallengeIcentive
                ? incentiveDetails.backgroundImage
                : backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
              <Picture
                image={incentiveDetails.image}
                alt={imageDescription}
                objectFitContain={true}
              />
            </OfferImageWrapper>
          )}

          <RewardText data-testid="incentive-name">{offerName}</RewardText>

          <PriceTextContainer>
            <SlashedPriceText>$13.69</SlashedPriceText>
            <DiscountedPriceText>$5.99</DiscountedPriceText>
          </PriceTextContainer>
        </ClickContainer>
      </OfferCardContainer>
    );
  }
);

export interface IExternalOfferCardV2 {
  handleClick: ILoyaltyRewardsViewProps['handleClick'];
  isFavorited: boolean;
  isFavoriteEnabled: boolean;
  isFavoriteLoading: boolean;
  handleToggleFavorite: (sanityId: string, name: string) => void;
  incentiveData: ReturnType<typeof parseExternalIncentiveData>;
  isOfferClosed: boolean;
  offerTier: LoyaltyTierKey;
}

export const ExternalOfferCardV2: FC<IExternalOfferCardV2> = memo(
  ({
    incentiveData,
    handleClick,
    isFavorited = false,
    isFavoriteEnabled = false,
    isFavoriteLoading = false,
    handleToggleFavorite: toggleFavorite = noop,
    isOfferClosed = false,
    offerTier: offerTierKey,
  }) => {
    const { formatMessage } = useIntl();
    const { isAuthenticated } = useAuthContext();
    const navigate = useNavigate();

    const { image, title, id, backgroundImage } = incentiveData;
    const { getContentForTier } = useFeaturesLoyaltyTiersContent();

    const sanityId = id;
    const offerName = title || '';

    const onFavoriteClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!isAuthenticated()) {
          return navigate(routes.signUp);
        }
        toggleFavorite(id, offerName);
      },
      [toggleFavorite, id, offerName, isAuthenticated, navigate]
    );

    const offerTierLabel = React.useMemo(() => {
      let tierKey: LoyaltyTierKey;

      // if there is no tier available in the offer, set Tier_1 as default
      if (!offerTierKey) {
        tierKey = LoyaltyTierKey.TIER_1;
      } else {
        tierKey = offerTierKey;
      }

      // if the offer has the initial tier required we don't want to show the tier name badge
      // unless the offer is locked
      if (tierKey === LoyaltyTierKey.TIER_1 && !isOfferClosed) {
        return '';
      }

      return getContentForTier(tierKey)?.loyaltyTiersUIConfigName?.locale;
    }, [offerTierKey, getContentForTier, isOfferClosed]);

    return (
      <OfferCardContainer
        data-testid={`external-offers-list-${isOfferClosed ? 'closed-offer-item' : 'item'}`}
      >
        <ClickContainer
          onClick={handleClick({
            incentiveName: offerName,
            incentiveSanityId: sanityId,
            isExternalOffer: true,
          })}
        >
          <LabelAndFavContainer>
            <LabelWrapper>
              {isAuthenticated() && isOfferClosed && (
                <LabelBadge>
                  <LockIconWrapper>
                    <LockIcon width="18.9px" height="14.4px" />
                  </LockIconWrapper>
                  <LabelText>{offerTierLabel}</LabelText>
                </LabelBadge>
              )}
            </LabelWrapper>
            {isFavoriteEnabled && (
              <FavButtonWrapper
                data-testid={isFavorited ? 'fav-icon-selected' : 'fav-icon'}
                onClick={onFavoriteClick}
                aria-label={formatMessage({
                  id: isFavorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
                })}
                isTransparent={true}
              >
                {isFavoriteLoading ? (
                  <FavouriteLoadingWrapper>
                    <FavoriteLoading height="2rem" width="2rem" />
                  </FavouriteLoadingWrapper>
                ) : (
                  <FavouriteIconWrapper>
                    <FavoriteIcon isFavorite={isFavorited} />
                  </FavouriteIconWrapper>
                )}
              </FavButtonWrapper>
            )}
          </LabelAndFavContainer>

          {image && (
            <OfferImageWrapper>
              {isAuthenticated() && isOfferClosed && <StyledRewardLock />}
              {backgroundImage && <BackgroundImage alt={title || ''} image={backgroundImage} />}
              <Picture image={image.locale} alt={title || ''} objectFitContain={true} />
            </OfferImageWrapper>
          )}

          <RewardText data-testid="external-offer-name">{title}</RewardText>
        </ClickContainer>
      </OfferCardContainer>
    );
  }
);
